/**
 * P4-COMMERCE — proposal ⇄ draft-order sync contract (deliverables 1-3).
 *
 * Laws (invoice-line-items.md §1-§5 + Decision 005 + Decision 001):
 * - Skafer has NO pricing engine: Shopify's draft order IS the pricing
 *   record. Skafer authorizes line items and displays Shopify's totals as
 *   the single source of truth.
 * - One draft order per order, revised IN PLACE (Tim 2026-09-25):
 *   revisions update the same order / same draft order — no forked
 *   proposal versions, no superseded-payment path. The revise-then-resend
 *   flow plus the intended-vs-synced divergence warning IS the guard.
 * - Idempotent operations: every sync carries an idempotency key derived
 *   from (orderId, intended set hash); duplicate clicks converge.
 * - Payment/order status is READ-ONLY in Skafer: last-synced timestamp +
 *   explicit failure state; payment authority stays in Shopify/Ullr.
 * - Line-item semantics (verified from lib/src/integration/shopify):
 *   variant_id / quantity / properties(_orderId,_itemType), one
 *   applied_discount slot, one shipping_line, taxes read-only.
 */

export const PROPOSAL_SYNC_VERSION = '0.1';

// ---------------------------------------------------------------------------
// Intended line-item set (what the proposal says the customer buys)
// ---------------------------------------------------------------------------

export type ItemType = 'Ski Package' | 'Binding' | 'Top Sheet' | 'Other';

export interface IntendedLineItem {
  /** Shopify variant id for catalog items; null for custom lines. */
  variantId: string | null;
  /** Display title (variant_title convention). */
  title: string;
  quantity: number;
  /** Unit price as quoted in the proposal (string decimal, Shopify form). */
  price: string;
  itemType: ItemType;
  /** Optional explicit taxable flag — ONLY valid for custom items. */
  taxable?: boolean;
}

export interface IntendedDiscount {
  description: string;
  valueType: 'percentage' | 'fixed_amount';
  value: string;
}

export interface IntendedShipping {
  /** custom=true carries price+title; carrier carries handle. */
  custom: boolean;
  price?: string;
  title?: string;
  handle?: string;
}

export interface IntendedOrderSet {
  orderId: number;
  /** The approved proposal version this set was quoted from (Decision 001 mutable semantics). */
  designVersion: number;
  items: IntendedLineItem[];
  discount: IntendedDiscount | null;
  shipping: IntendedShipping | null;
}

// ---------------------------------------------------------------------------
// Synced (actual) draft-order state — what Shopify says today
// ---------------------------------------------------------------------------

export type PaymentStatus = 'PENDING' | 'PAID' | 'PARTIALLY_PAID' | 'REFUNDED' | 'VOIDED' | 'NOT_REQUIRED';

export interface SyncedDraftOrder {
  shopifyDraftOrderId: string;
  wagnerOrderIds: number[];
  items: IntendedLineItem[];
  discount: IntendedDiscount | null;
  shipping: IntendedShipping | null;
  totalPrice: string;            // Shopify's number — display source of truth
  financialStatus: PaymentStatus;
  invoiceSentAt: string | null;
  /** The draft-order invoice URL (pay/check-out page) - Shopify generates it for every draft. */
  invoiceUrl?: string;
  lastSyncedAt: string;
}

// ---------------------------------------------------------------------------
// Divergence: intended vs synced (the stale-state warning)
// ---------------------------------------------------------------------------

export interface Divergence {
  field: 'items' | 'discount' | 'shipping';
  detail: string;
}

/** Stable, order-insensitive comparison of the line-item sets. */
/**
 * Compare decimal strings numerically ("10" == "10.0" == "10.00"); Shopify
 * normalizes values on write-back, so text comparison false-positives.
 */
function sameDecimal(x: string, y: string): boolean {
  const fx = Number.parseFloat(x);
  const fy = Number.parseFloat(y);
  if (Number.isFinite(fx) && Number.isFinite(fy)) return fx === fy;
  return x === y;
}

export function itemsDiffer(a: IntendedLineItem[], b: IntendedLineItem[]): boolean {
  const norm = (items: IntendedLineItem[]): string[] =>
    items
      .map((i) => `${i.itemType}|${i.variantId ?? 'custom'}|${i.title}|${i.quantity}|${Number.parseFloat(i.price) || i.price}`)
      .sort();
  const na = norm(a), nb = norm(b);
  return na.length !== nb.length || na.some((v, i) => v !== nb[i]);
}

export function diffDiscount(a: IntendedDiscount | null, b: IntendedDiscount | null): boolean {
  if (a === b) return false;
  if (!a || !b) return true;
  return a.description !== b.description || a.valueType !== b.valueType || !sameDecimal(a.value, b.value);
}

export function diffShipping(a: IntendedShipping | null, b: IntendedShipping | null): boolean {
  if (a === b) return false;
  if (!a || !b) return true;
  return a.custom !== b.custom || !sameDecimal(a.price ?? '', b.price ?? '') || a.title !== b.title || a.handle !== b.handle;
}

export function computeDivergence(intended: IntendedOrderSet, synced: SyncedDraftOrder): Divergence[] {
  const out: Divergence[] = [];
  if (itemsDiffer(intended.items, synced.items)) {
    out.push({ field: 'items', detail: 'draft-order line items no longer match the approved proposal version' });
  }
  if (diffDiscount(intended.discount, synced.discount)) {
    out.push({ field: 'discount', detail: 'discount differs between proposal and draft order' });
  }
  if (diffShipping(intended.shipping, synced.shipping)) {
    out.push({ field: 'shipping', detail: 'shipping differs between proposal and draft order' });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Idempotent sync plan + result (deliverable 2)
// ---------------------------------------------------------------------------

/** Deterministic idempotency key: (orderId, designVersion, intended set). */
export function syncIdempotencyKey(intended: IntendedOrderSet): string {
  let h = 0x811c9dc5;
  const content = JSON.stringify({
    o: intended.orderId,
    v: intended.designVersion,
    i: [...intended.items]
      .map((it) => `${it.itemType}|${it.variantId ?? 'custom'}|${it.title}|${it.quantity}|${it.price}`)
      .sort(),
    d: intended.discount,
    s: intended.shipping,
  });
  for (let i = 0; i < content.length; i++) {
    h ^= content.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `sync-${intended.orderId}-v${intended.designVersion}-${h.toString(16)}`;
}

export type SyncStatus = 'planned' | 'applied' | 'applied-noop' | 'failed';

export interface SyncPlan {
  key: string;
  /**_operations the adapter must run against Shopify, in order._ */
  operations: Array<
    | { op: 'replace-items'; items: IntendedLineItem[] }
    | { op: 'set-discount'; discount: IntendedDiscount | null }
    | { op: 'set-shipping'; shipping: IntendedShipping | null }
    | { op: 'noop' }
  >;
}

/** Build the minimal in-place revision plan (Tim 2026-09-25: same draft order). */
export function planSync(intended: IntendedOrderSet, synced: SyncedDraftOrder | null): SyncPlan {
  const key = syncIdempotencyKey(intended);
  const ops: SyncPlan['operations'] = [];
  if (!synced || itemsDiffer(intended.items, synced.items)) {
    ops.push({ op: 'replace-items', items: intended.items });
  }
  if (!synced || diffDiscount(intended.discount, synced.discount)) {
    ops.push({ op: 'set-discount', discount: intended.discount });
  }
  if (!synced || diffShipping(intended.shipping, synced.shipping)) {
    ops.push({ op: 'set-shipping', shipping: intended.shipping });
  }
  return { key, operations: ops.length ? ops : [{ op: 'noop' }] };
}

export interface SyncResult {
  key: string;
  status: SyncStatus;
  appliedAt: string;
  shopifyDraftOrderId: string;
  /** The synced snapshot after applying (adapter fills from Shopify). */
  synced: SyncedDraftOrder;
  error?: string;
}

/**
 * Apply a plan idempotently. The adapter executes against Shopify; when the
 * same key already applied cleanly, the retry is a no-op (duplicate-click
 * convergence). This function is the CONTRACT over the adapter boundary.
 */
export function applySync(
  plan: SyncPlan,
  execute: (plan: SyncPlan) => { synced: SyncedDraftOrder } | { error: string },
  attemptedKeys: Set<string>,
  at: string,
): SyncResult {
  // Idempotency: if this exact key already applied, converge to no-op.
  if (attemptedKeys.has(plan.key)) {
    const noopOps: SyncPlan['operations'] = [{ op: 'noop' }];
    const executed = execute({ ...plan, operations: noopOps });
    if ('error' in executed) {
      return { key: plan.key, status: 'failed', appliedAt: at, shopifyDraftOrderId: '', synced: null as never, error: executed.error };
    }
    return { key: plan.key, status: 'applied-noop', appliedAt: at, shopifyDraftOrderId: executed.synced.shopifyDraftOrderId, synced: executed.synced };
  }
  attemptedKeys.add(plan.key);
  const executed = execute(plan);
  if ('error' in executed) {
    // Failed syncs release the key so a retry (possibly with changed input) can run.
    attemptedKeys.delete(plan.key);
    return { key: plan.key, status: 'failed', appliedAt: at, shopifyDraftOrderId: '', synced: null as never, error: executed.error };
  }
  return { key: plan.key, status: 'applied', appliedAt: at, shopifyDraftOrderId: executed.synced.shopifyDraftOrderId, synced: executed.synced };
}

// ---------------------------------------------------------------------------
// Read-only payment status display (deliverable 3)
// ---------------------------------------------------------------------------

export interface PaymentStatusView {
  shopifyDraftOrderId: string;
  paymentStatus: PaymentStatus;
  totalPrice: string;
  lastSyncedAt: string;
  /** Explicit failure state — never silently stale. */
  syncHealthy: boolean;
  divergences: Divergence[];
}

export function paymentStatusView(
  intended: IntendedOrderSet,
  synced: SyncedDraftOrder | null,
  syncFailed: string | null,
): PaymentStatusView {
  if (!synced) {
    return {
      shopifyDraftOrderId: '',
      paymentStatus: 'PENDING',
      totalPrice: '—',
      lastSyncedAt: '',
      syncHealthy: false,
      divergences: [{ field: 'items', detail: syncFailed ?? 'draft order not yet synced for this proposal' }],
    };
  }
  return {
    shopifyDraftOrderId: synced.shopifyDraftOrderId,
    paymentStatus: synced.financialStatus,
    totalPrice: synced.totalPrice, // Shopify's number — display verbatim
    lastSyncedAt: synced.lastSyncedAt,
    syncHealthy: syncFailed === null,
    divergences: computeDivergence(intended, synced),
  };
}